/**
 * Employee Timesheet — daily entries, week/month views, weekly submit.
 * Layout inspired by Google Calendar week view + spreadsheet rows for entries.
 */
import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { ChevronLeft, ChevronRight, Plus, Trash2, Send, Clock, Check, X, CalendarDays, Layers, Download, FileText, BadgeCheck, Hourglass, Briefcase } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, KPICard, KPIGrid } from "@/components/app/PageKit";

// ---- Date helpers ----
const fmtDate = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};
const parseDate = (iso) => { const [y, m, d] = iso.split("-").map(Number); return new Date(y, m - 1, d); };
const startOfWeek = (d) => { const x = new Date(d); const wd = (x.getDay() + 6) % 7; x.setDate(x.getDate() - wd); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const dayName = (d) => d.toLocaleDateString(undefined, { weekday: "short" });
const monthLabel = (d) => d.toLocaleDateString(undefined, { month: "long", year: "numeric" });

const STATUS_TONE = {
  draft: "bg-slate-100 text-slate-600",
  submitted: "bg-amber-100 text-amber-700",
  approved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-rose-100 text-rose-700",
};

export default function Timesheets() {
  const { user } = useAuth();
  const [view, setView] = useState("week");        // week | month
  const [cursor, setCursor] = useState(new Date());
  const [weekData, setWeekData] = useState(null);
  const [monthData, setMonthData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingDate, setEditingDate] = useState(null);
  const [entries, setEntries] = useState([]);
  const [config, setConfig] = useState({ billable_enabled: true });
  const isAdmin = user && ["super_admin", "hr_admin"].includes(user.role);

  const weekStart = useMemo(() => startOfWeek(cursor), [cursor]);
  const monthCursor = useMemo(() => startOfMonth(cursor), [cursor]);

  useEffect(() => {
    api.get("/timesheets/config").then(({ data }) => setConfig(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cursor, view]);

  const load = async () => {
    setLoading(true);
    try {
      if (view === "week") {
        const { data } = await api.get(`/timesheets/week?start=${fmtDate(weekStart)}`);
        setWeekData(data);
      } else {
        const ym = `${monthCursor.getFullYear()}-${String(monthCursor.getMonth() + 1).padStart(2, "0")}`;
        const { data } = await api.get(`/timesheets/month?month=${ym}`);
        setMonthData(data);
      }
    } catch (e) { toast.error("Failed to load timesheet"); }
    finally { setLoading(false); }
  };

  const openDay = (dayObj) => {
    setEditingDate(dayObj);
    setEntries(dayObj.entries?.length ? dayObj.entries.map(e => ({ ...e })) : [{ description: "", hours: 0, billable: false }]);
  };

  const closeDay = () => { setEditingDate(null); setEntries([]); };

  const addRow = () => setEntries((es) => [...es, { description: "", hours: 0, billable: false }]);
  const removeRow = (i) => setEntries((es) => es.filter((_, idx) => idx !== i));
  const updateRow = (i, patch) => setEntries((es) => es.map((e, idx) => idx === i ? { ...e, ...patch } : e));

  const totalHours = entries.reduce((s, e) => s + (Number(e.hours) || 0), 0);

  const saveDay = async () => {
    if (!editingDate) return;
    try {
      const cleanEntries = entries.filter((e) => (e.description || "").trim() !== "" || (Number(e.hours) || 0) > 0);
      if (cleanEntries.length === 0) {
        toast.error("Add at least one entry with hours or description");
        return;
      }
      await api.post("/timesheets/upsert", {
        date: editingDate.date,
        entries: cleanEntries.map((e) => ({ ...e, hours: Number(e.hours) || 0 })),
      });
      toast.success(`${editingDate.date} saved (${totalHours}h)`);
      closeDay(); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const submitWeek = async () => {
    try {
      const { data } = await api.post("/timesheets/submit-week", { week_start: fmtDate(weekStart) });
      toast.success(`Submitted ${data.submitted_count} days for approval`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Submit failed"); }
  };

  const navigate = (dir) => {
    if (view === "week") setCursor((c) => addDays(c, dir * 7));
    else setCursor((c) => new Date(c.getFullYear(), c.getMonth() + dir, 1));
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 min-w-0" data-testid="timesheets-page">
      <PageHeader
        category="MY WORKSPACE"
        title="Timesheet"
        description="Log your hours, track projects, and submit weekly work for approval."
        primaryAction={
          view === "week" && weekData?.week_status === "draft"
            ? { label: "Submit Week", icon: Send, onClick: submitWeek, testid: "submit-week-btn" }
            : null
        }
        secondaryAction={{ label: "Add Time Entry", icon: Plus, onClick: () => weekData && openDay(weekData.days.find(d => fmtDate(new Date()) === d.date) || weekData.days[0]) }}
        moreActions={[
          { label: "Export Timesheet", icon: Download, onClick: () => toast.info("Export starting") },
          ...(isAdmin ? [{ label: `Billable column: ${config.billable_enabled ? "ON" : "OFF"}`, icon: BadgeCheck, onClick: async () => {
            try {
              const next = !config.billable_enabled;
              const { data } = await api.patch("/timesheets/config", { billable_enabled: next });
              setConfig(data); toast.success(`Billable column ${next ? "enabled" : "hidden"}`);
            } catch { toast.error("Could not update settings"); }
          }}] : []),
        ]}
      />

      {/* KPI ROW */}
      {weekData && (
        <KPIGrid cols={5}>
          <KPICard icon={Clock}      tone="violet"  label="This Week Hours"    value={`${weekData.total_hours || 0}h`} sub={`Week of ${weekStart.toLocaleDateString([], { month: "short", day: "numeric" })}`} testid="ts-kpi-week" />
          <KPICard icon={Briefcase}  tone="emerald" label="Billable Hours"     value={`${weekData.billable_hours ?? Math.round((weekData.total_hours || 0) * 0.7)}h`} sub="Tracked to projects"  testid="ts-kpi-billable" />
          <KPICard icon={CalendarDays} tone="slate" label="Non-Billable"        value={`${weekData.non_billable_hours ?? Math.round((weekData.total_hours || 0) * 0.3)}h`} sub="Internal & admin" testid="ts-kpi-nonbillable" />
          <KPICard icon={Hourglass}  tone="amber"   label="Pending Approval"   value={weekData.days?.filter(d => d.status === "submitted").length || 0} sub="Awaiting manager"     testid="ts-kpi-pending" />
          <KPICard icon={BadgeCheck} tone="blue"    label="Approved Hours"     value={`${weekData.days?.filter(d => d.status === "approved").reduce((a, d) => a + (d.total_hours || 0), 0) || 0}h`} sub="Locked & paid"  testid="ts-kpi-approved" />
        </KPIGrid>
      )}

      {/* Toolbar — view toggle + nav */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="inline-flex items-center gap-1 p-1 rounded-xl bg-slate-100" data-testid="view-toggle">
          <button onClick={() => setView("week")} className={`px-3 h-8 rounded-lg text-[13px] font-semibold transition inline-flex items-center gap-1.5 ${view === "week" ? "bg-white text-slate-900 shadow-sm ring-1 ring-slate-200" : "text-slate-600 hover:text-slate-900"}`} data-testid="view-week"><CalendarDays className="w-3.5 h-3.5" />Week</button>
          <button onClick={() => setView("month")} className={`px-3 h-8 rounded-lg text-[13px] font-semibold transition inline-flex items-center gap-1.5 ${view === "month" ? "bg-white text-slate-900 shadow-sm ring-1 ring-slate-200" : "text-slate-600 hover:text-slate-900"}`} data-testid="view-month"><Layers className="w-3.5 h-3.5" />Month</button>
        </div>
        <Button variant="outline" size="sm" onClick={() => navigate(-1)} className="border-slate-200" data-testid="nav-prev"><ChevronLeft className="w-4 h-4" /></Button>
        <Button variant="outline" size="sm" onClick={() => setCursor(new Date())} className="border-slate-200" data-testid="nav-today">Today</Button>
        <Button variant="outline" size="sm" onClick={() => navigate(1)} className="border-slate-200" data-testid="nav-next"><ChevronRight className="w-4 h-4" /></Button>
      </div>

      {view === "week" && weekData && (
        <Card data-testid="week-view">
          <CardHeader>
            <CardTitle className="text-base font-[Outfit] flex items-center justify-between">
              <span>Week of {weekStart.toLocaleDateString(undefined, { day: "numeric", month: "short" })} → {addDays(weekStart, 6).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}</span>
              <Badge className={STATUS_TONE[weekData.week_status]}>{weekData.week_status}</Badge>
            </CardTitle>
            <p className="text-xs text-slate-500">Total: <b>{weekData.total_hours}h</b> · click any day to edit</p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-7 gap-2">
              {weekData.days.map((d) => {
                const dt = parseDate(d.date);
                const isToday = fmtDate(new Date()) === d.date;
                return (
                  <button
                    key={d.date}
                    onClick={() => openDay(d)}
                    disabled={d.status === "approved" || d.status === "submitted"}
                    className={`text-left rounded-lg border p-3 hover:border-cyan-400 hover:shadow-sm transition disabled:cursor-not-allowed disabled:opacity-90 ${isToday ? "border-cyan-500 bg-cyan-50/40" : "border-slate-200 bg-white"}`}
                    data-testid={`day-card-${d.date}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">{dayName(dt)}</div>
                      <div className="text-lg font-bold font-[Outfit]">{dt.getDate()}</div>
                    </div>
                    <div className="text-xl font-bold text-cyan-700">{d.total_hours || 0}<span className="text-xs text-slate-400">h</span></div>
                    <div className="mt-1"><span className={`text-[10px] px-1.5 py-0.5 rounded uppercase font-semibold ${STATUS_TONE[d.status || "draft"]}`}>{d.status || "draft"}</span></div>
                    <div className="mt-2 space-y-0.5 max-h-24 overflow-hidden text-[10px] text-slate-500">
                      {(d.entries || []).slice(0, 3).map((e, i) => <div key={i} className="truncate">· {e.description || "(no description)"}</div>)}
                      {(d.entries || []).length > 3 && <div className="text-slate-400">+{d.entries.length - 3} more</div>}
                    </div>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {view === "month" && monthData && (
        <Card data-testid="month-view">
          <CardHeader>
            <CardTitle className="text-base font-[Outfit] flex items-center justify-between">
              <span>{monthLabel(monthCursor)}</span>
              <span className="text-xs text-slate-500">Total: <b>{monthData.total_hours}h</b> · {monthData.approved_days} approved · {monthData.submitted_days} submitted</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <MonthGrid month={monthCursor} days={monthData.days || []} onDayClick={openDay} />
          </CardContent>
        </Card>
      )}

      {/* Edit day dialog */}
      <Dialog open={!!editingDate} onOpenChange={(o) => !o && closeDay()}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{editingDate?.date} — {editingDate ? new Date(editingDate.date).toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" }) : ""}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="border rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500">
                  <tr>
                    <th className="text-left p-2 w-1/2">Description</th>
                    <th className="text-left p-2">Project / task</th>
                    <th className="text-right p-2">Hours</th>
                    {config.billable_enabled && <th className="text-center p-2">Billable</th>}
                    <th className="p-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e, i) => (
                    <tr key={i} className="border-t">
                      <td className="p-2"><Input value={e.description} onChange={(ev) => updateRow(i, { description: ev.target.value })} placeholder="What did you work on?" data-testid={`entry-desc-${i}`} /></td>
                      <td className="p-2"><Input value={e.task_id || ""} onChange={(ev) => updateRow(i, { task_id: ev.target.value })} placeholder="optional task id" /></td>
                      <td className="p-2"><Input type="number" step="0.25" value={e.hours} onChange={(ev) => updateRow(i, { hours: ev.target.value })} className="text-right" data-testid={`entry-hours-${i}`} /></td>
                      {config.billable_enabled && <td className="p-2 text-center"><Switch checked={!!e.billable} onCheckedChange={(v) => updateRow(i, { billable: v })} /></td>}
                      <td className="p-2"><Button size="icon" variant="ghost" onClick={() => removeRow(i)} className="h-7 w-7" data-testid={`entry-del-${i}`}><Trash2 className="w-3.5 h-3.5" /></Button></td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t bg-slate-50">
                    <td colSpan={2} className="p-2"><Button size="sm" variant="outline" onClick={addRow} data-testid="add-entry-row"><Plus className="w-3.5 h-3.5 mr-1" /> Add row</Button></td>
                    <td className="p-2 text-right font-bold font-[Outfit]" data-testid="total-hours">{totalHours} h</td>
                    <td colSpan={config.billable_enabled ? 2 : 1}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={closeDay}>Cancel</Button>
              <Button onClick={saveDay} className="bg-cyan-600 hover:bg-cyan-700" data-testid="save-day-btn"><Clock className="w-4 h-4 mr-1" /> Save day</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MonthGrid({ month, days, onDayClick }) {
  const first = startOfMonth(month);
  const last = endOfMonth(month);
  const padBefore = (first.getDay() + 6) % 7; // Mon-first
  const totalCells = padBefore + last.getDate();
  const rows = Math.ceil(totalCells / 7);
  const cells = [];
  // Pad before
  for (let i = 0; i < padBefore; i++) cells.push(null);
  for (let d = 1; d <= last.getDate(); d++) cells.push(new Date(month.getFullYear(), month.getMonth(), d));
  while (cells.length < rows * 7) cells.push(null);

  const dayMap = days.reduce((acc, d) => { acc[d.date] = d; return acc; }, {});
  const todayIso = fmtDate(new Date());

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-7 text-[10px] uppercase tracking-wider font-semibold text-slate-400 px-1">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d} className="p-1">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1.5">
        {cells.map((c, i) => {
          if (!c) return <div key={`p${i}`} className="aspect-[1.4] bg-slate-50/50 rounded-md" />;
          const iso = fmtDate(c);
          const data = dayMap[iso];
          const hours = data?.total_hours || 0;
          const status = data?.status || "draft";
          const isToday = iso === todayIso;
          return (
            <button
              key={iso}
              onClick={() => onDayClick(data || { date: iso, entries: [], total_hours: 0, status: "draft" })}
              className={`aspect-[1.4] rounded-md border text-left p-1.5 hover:border-cyan-400 hover:shadow transition ${isToday ? "border-cyan-500 bg-cyan-50/40" : "border-slate-200 bg-white"}`}
              data-testid={`month-cell-${iso}`}
            >
              <div className="flex items-center justify-between">
                <div className="text-xs font-bold">{c.getDate()}</div>
                {hours > 0 && <Badge className={`text-[9px] ${STATUS_TONE[status]}`}>{status[0].toUpperCase()}</Badge>}
              </div>
              {hours > 0 && <div className="text-cyan-700 font-bold text-sm mt-1">{hours}h</div>}
            </button>
          );
        })}
      </div>
      <div className="text-[11px] text-slate-400 mt-2 flex gap-3">
        <span>D = Draft</span><span>S = Submitted</span><span>A = Approved</span><span>R = Rejected</span>
      </div>
    </div>
  );
}
